import React, { useRef, useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Camera, X, RotateCcw, Loader2 } from "lucide-react";

export default function CameraCapture({ onCapture, onCancel }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");

  const startCamera = useCallback(async (facing) => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facing,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          setIsCameraReady(true);
          setError(null);
        };
      }
    } catch (err) {
      console.error("Camera error:", err);
      setError("Camera access denied. Please allow camera permissions.");
      setIsCameraReady(false);
    }
  }, []);

  useEffect(() => {
    startCamera(facingMode);
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, [facingMode, startCamera]);

  const captureImage = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    const base64 = dataUrl.split(",")[1];

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }

    onCapture(base64);
  };

  const toggleFacing = () => {
    setFacingMode((prev) => (prev === "environment" ? "user" : "environment"));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2
          className="text-lg font-extrabold text-slate-900"
          style={{ fontFamily: "Nunito, sans-serif" }}
        >
          Camera
        </h2>
        <Button
          data-testid="cancel-camera-btn"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="text-slate-500 hover:text-red-500 rounded-full"
        >
          <X className="w-4 h-4 mr-1" />
          Cancel
        </Button>
      </div>

      {error ? (
        <div className="rounded-2xl bg-red-50 border border-red-100 p-8 text-center">
          <p className="text-red-600 font-medium mb-4">{error}</p>
          <Button
            onClick={() => startCamera(facingMode)}
            className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6"
          >
            Retry
          </Button>
        </div>
      ) : (
        <div className="camera-viewfinder relative bg-slate-900 rounded-2xl overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-auto rounded-2xl"
            data-testid="camera-video"
          />

          {!isCameraReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
                <p className="text-white/70 text-sm">Starting camera...</p>
              </div>
            </div>
          )}

          {/* Scan line effect */}
          {isCameraReady && (
            <div className="absolute inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-indigo-400 to-transparent animate-scan-line opacity-60 pointer-events-none" />
          )}

          {/* Controls overlay */}
          {isCameraReady && (
            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
              <Button
                data-testid="flip-camera-btn"
                variant="ghost"
                size="sm"
                onClick={toggleFacing}
                className="bg-white/20 backdrop-blur-md text-white hover:bg-white/30 rounded-full w-10 h-10 p-0"
              >
                <RotateCcw className="w-5 h-5" />
              </Button>
              <button
                data-testid="capture-btn"
                onClick={captureImage}
                className="w-16 h-16 rounded-full bg-white border-4 border-indigo-400 hover:border-indigo-500 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center animate-pulse-glow"
              >
                <Camera className="w-7 h-7 text-indigo-600" />
              </button>
              <div className="w-10 h-10" /> {/* Spacer for centering */}
            </div>
          )}
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
